<template>
  <div class="form">
    <FormDate
      :lang="lang"
      :ln="ln"
      :locale="locale"
      :info="info"
      :permission="permission"
      :roomKey="roomKey"
      :rid="rid"
      :guestInfo="guestInfo"
      :isStaff="isStaff"
      :isPublic="isPublic"
      :reserveDate="reserve.date"
      :cancelDate="cancelDate"
      @setDate="setDate" />
    <FormTime
      v-if="validate.date"
      :lang="lang"
      :ln="ln"
      :locale="locale"
      :info="info"
      :roomKey="roomKey"
      :rid="rid"
      :isStaff="isStaff"
      :isPublic="isPublic"
      :guestInfo="guestInfo"
      :reserveDate="reserve.date"
      :reserveTime="reserve.time"
      :timeInfo="info.time"
      @setTime="setTime" />
    <FormPerson
      v-if="validate.date"
      :lang="lang"
      :ln="ln"
      :locale="locale"
      :info="info"
      :roomKey="roomKey"
      :rid="rid"
      :isPublic="isPublic"
      :reservePerson="reserve.person"
      @setPerson="setPerson" />
    <FormSection
      v-if="validate.date && validate.time && validate.person"
      :lang="lang"
      :ln="ln"
      :locale="locale"
      :info="info"
      :roomKey="roomKey"
      :rid="rid"
      :isPublic="isPublic"
      :sectionInfo="info.section"
      @setSection="setSection" />
    <div v-if="validate.section && place">
      <p v-if="!place.selectable" class="noData toCapitalize notranslate">
        {{locale.nodata_reservation_section}}
      </p>
      <FormPlace
        v-else-if="!seatMap"
        :lang="lang"
        :ln="ln"
        :locale="locale"
        :info="info"
        :roomKey="roomKey"
        :rid="rid"
        :isPublic="isPublic"
        :placeInfo="place.list"
        :isSelectable="place.selectable"
        :isStaff="isStaff"
        :seatCount="seatCount"
        @setPlace="setPlace" />
      <FormSeat
        v-else
        :lang="lang"
        :ln="ln"
        :locale="locale"
        :info="info"
        :roomKey="roomKey"
        :rid="rid"
        :isPublic="isPublic"
        :seatMap="seatMap"
        :seatCount="seatCount"
        :remainSeat="remainSeat"
        @setSeatMap="setSeats" />
    </div>
    <p v-if="checkDateMessage" class="noData toCapitalize notranslate">
      {{checkDateMessage}}
    </p>
    <p v-else-if="!validate.date" class="noData toCapitalize notranslate">
      {{locale.message_select_date}}
    </p>
    <p v-else-if="!validate.time" class="noData toCapitalize notranslate">
      {{locale.message_select_time}}
    </p>
    <p v-else-if="!validate.person" class="noData toCapitalize notranslate">
      {{locale.message_select_person}}
    </p>
    <FormUserInfo
      v-if="validate.seat && checkComplete"
      :lang="lang"
      :ln="ln"
      :locale="locale"
      :info="info"
      :roomKey="roomKey"
      :rid="rid"
      :guestInfo="guestInfo"
      :isPublic="isPublic"
      @setUserInfo="setUserInfo" />
    <div v-if="info.infoTxt[lang].length">
      <pre v-html="info.infoTxt[lang]" class="messageBox toCapitalize"></pre>
    </div>
    <div class="formGroup btns mt30 notranslate">
      <button
        :disabled="!validate.result"
        type="button"
        class="btn btnRound btnFull toCapitalize"
        @click="confirmSummary">
        {{locale.reservation_confirm}}
      </button>
    </div>
    <!-- 예약내용 레이어 -->
    <Summary
      v-if="summary"
      :locale="locale"
      :lang="lang"
      :summary="summary"
      @close="summary = null"
      @confirm="submit" />
    <!-- //예약내용 레이어 -->
  </div>
</template>

<script>
import moment from 'moment/moment'
import regExp from '@/common/regExp'
import Summary from '@/components/Reservation/Summary'
import SeatMap from '@/components/Reservation/SeatMap'
import FormDate from '@/components/Reservation/FormDate'
import FormTime from '@/components/Reservation/FormTime'
import FormPerson from '@/components/Reservation/FormPerson'
import FormSection from '@/components/Reservation/FormSection'
import FormPlace from '@/components/Reservation/FormPlace'
import FormSeat from '@/components/Reservation/FormSeat'
import FormUserInfo from '@/components/Reservation/FormUserInfo'

export default {
  name: 'ReservationForm',
  props: [
    'lang',
    'ln',
    'locale',
    'info',
    'roomKey',
    'rid',
    'isPublic'
  ],
  components: {
    Summary,
    SeatMap,
    FormDate,
    FormTime,
    FormPerson,
    FormSection,
    FormPlace,
    FormSeat,
    FormUserInfo
  },
  computed: {
    isStaff () {
      const staff = this.$store.state.authentication.staff
      return staff
    },
    guestInfo () {
      return this.$store.getters['guest/getGuestInfo']
    },
    dateFormat () {
      return this.$store.getters['common/getDateFormat']
    },
    readyInfo () {
      return this.reserve.date && this.reserve.time && this.reserve.person
    },
    section () { // 선택 가능한 section, 선택된 section
      const sectionId = this.select.section
      const list = (sectionId && this.info && this.info.section) || []
      const active = (list && list.find(x => x.id === sectionId)) || null
      return {
        list: list,
        active: active
      }
    },
    place () { // 선택 가능한 place, 선택된 place
      const isStaff = this.isStaff
      const isMultiple = this.info.multiple
      const sectionId = this.section && this.section.active && this.section.active.id
      const placeId = this.select.place
      const section = sectionId && this.info && this.info.section && this.info.section.find(x => x.id === sectionId)
      const autoAssign = section && section.autoAssign
      const list = (section && section.place) || []
      const active = (list && list.find(x => x.placeId === placeId)) || null
      const person = this.reserve.person || 0
      const seatCount = this.seatCount
      const remainSeat = this.remainSeat
      const placeList = (list && list.map(x => {
        const isDisabled = isStaff // staff는 인원수 관계 없이 설정 가능, guest는 인원수제한 체크, 남아있는 자리 체크
          ? false
          : autoAssign // 자동배정?
            ? isMultiple // 자동배정
              ? (x.min > seatCount || x.max < seatCount) || (remainSeat && remainSeat[x.placeId] && remainSeat[x.placeId].remainSeats.length < seatCount) // 인원수만큼 자리가 남았는지?
              : remainSeat && remainSeat[x.placeId] && remainSeat[x.placeId].remainSeats.length <= 0 // 1자리 이상 남았는지?
            : isMultiple // !자동배정
              ? Object.keys(remainSeat).map(x => remainSeat[x] && remainSeat[x].remainSeats && remainSeat[x].remainSeats.length).reduce((a, b) => a + b, 0) < seatCount // place 전체에서 인원수만큼 자리가 남았는지?
              : Object.keys(remainSeat).filter(x => remainSeat[x].remainSeats && remainSeat[x].remainSeats.length > 0).length <= 0 // place 전체에서 1자리 이상 남았는지?
        return {
          ...x,
          current: (remainSeat && remainSeat[x.placeId] && remainSeat[x.placeId].current) || 0, // 조회된 예약수
          disabled: x.disabled || isDisabled,
          person: person
        }
      })) || []
      return {
        list: placeList,
        selectable: placeList.filter(x => !x.disabled).length > 0,
        active: active
      }
    },
    seat () {
      const place = this.place && this.place.active
      const list = (place && place.seat) || []
      const remainSeat = this.remainSeat
      return {
        list: list && list.map(x => {
          const remainSeats = remainSeat[x.placeId] && remainSeat[x.placeId].remainSeats
          return {
            ...x,
            disabled: remainSeats && !remainSeats.includes(x.seatId)
          }
        })
      }
    },
    seatMap () { // 자리 직접선택에 필요한 정보 맵핑
      const section = this.section && this.section.active
      return section && !section.autoAssign
        ? {
          seats: section.seatMap,
          seatObj: section.seatObj,
          info: {
            showPlaceName: section.showPlaceName,
            showSeatName: section.showSeatName,
            useSeatMap: section.useSeatMap
          }
        }
        : null
    },
    seatCount () { // info.multiple ? 인원수대로 자리 지정(수영장 입장) : 한자리(레스토랑 테이블)
      const person = parseInt(this.reserve.person)
      return this.info.multiple ? person : 1
    },
    validate () {
      const isStaff = this.isStaff
      const isDate = isVal(this.reserve.date)
      const isTime = this.reserve.time && isVal(this.reserve.time.id)
      const isPerson = isVal(this.reserve.person)
      const isSection = this.section && this.section.active && isVal(this.section.active.id)
      const isPlace = this.place && this.place.active && isVal(this.place.active)
      const isSeat = this.reserve.seats && this.reserve.seats.length > 0
      const isFirstName = isVal(this.reserve.firstName)
      const isLastName = isVal(this.reserve.lastName)
      const isPhone = this.info.bookerTel ? isVal(this.reserve.phone) && this.validatePhone : true
      const isEmail = this.info.bookerEmail ? isVal(this.reserve.email) : true
      const isUserInfo = isFirstName && isLastName && isPhone && isEmail
      function isVal (str) {
        return regExp.replaceAllEmpty(str || '').length > 0
      }
      return {
        isStaff: isStaff,
        date: isDate,
        time: isTime,
        person: isPerson,
        section: isSection,
        place: isPlace,
        seat: isSeat,
        firstName: isFirstName,
        lastName: isLastName,
        phone: isPhone,
        email: isEmail,
        result: isDate && isTime && isSection && isPlace && isSeat && isUserInfo
      }
    }
  },
  watch: {
    place: {
      deep: true,
      handler (data) {
        if (!data.selectable) {
          this.select.place = null
          this.reserve.seats = null
          this.checkComplete = false
        }
      }
    }
  },
  created () {
    if (this.info.disabled) {
      this.checkDateMessage = this.locale.message_reservation_disabled
    } else if (!this.isStaff && this.info.availableType) {
      if (this.info.availableType === 'ota' && !this.roomKey) { // 기본값 투숙객(ota) 검사
        this.checkDateMessage = this.locale.alert_reservation_permission
      } else if (this.info.availableType === 'rid' && (!this.rid && !this.roomKey)) { // 예약자까지 사용 가능하면 예약+투숙 모두 포함하여 ota 또는 rid 검사
        this.checkDateMessage = this.locale.alert_reservation_permission
      } else {
        this.permission = true
      }
    } else {
      this.permission = true
    }
  },
  data () {
    let userInfo = {
      ...this.info.bookerEmail && {
        email: null
      },
      ...this.info.bookerTel && {
        phone: null
      }
    }
    let validatePhone = this.info.bookerTel ? false : true
    return {
      permission: false,
      select: {
        section: null,
        place: null
      },
      reserve: {
        date: null,
        time: null,
        person: null,
        seats: null,
        firstName: null,
        lastName: null,
        ...userInfo
      },
      validatePhone,
      remainSeat: null,
      checkDateMessage: null,
      checkComplete: false,
      summary: null,
      cancelDate: null
    }
  },
  methods: {
    getResultData (code) {
      return this.$store.getters['reservation/getReservationResultData'](code)
    },
    setDateFormat (date) {
      return moment(date).format(this.dateFormat.toUpperCase())
    },
    async setDate (date) { // 날짜별 예약가능여부 체크
      this.$store.commit('loading', true)
      const checkDate = await this.$store.dispatch('reservation/checkReservationDate', {
        property: this.info.id,
        date: date
      })
      if (checkDate && checkDate.code) {
        const code = this.getResultData(checkDate.code)
        if (checkDate.code === 200) {
          this.checkDateMessage = null
          this.reserve.date = date
        } else {
          this.checkDateMessage = this.locale[`alert_reservation_${code === 'already_self' ? 'already_self_date' : code}`]
          this.cancelDate = date
          this.reserve.date = null
          this.setSection()
        }
      } else {
        this.checkDateMessage = null
        this.cancelDate = date
        this.reserve.date = null
        this.setSection()
        alert(this.locale.alert_data_load_fail)
      }
      this.reserve.time = null
      this.reserve.person = null
      this.setSection()
      this.$store.commit('loading', false)
      setTimeout(() => {
        this.cancelDate = null
      }, 500)
    },
    setTime (time) {
      this.reserve.time = time
      if (this.select.section) {
        this.setSection(this.select.section)
        if (this.select.place) {
          this.seatSeatAssign()
        }
      }
    },
    setPerson (person) {
      this.reserve.person = parseInt(person)
      if (this.select.section) {
        this.setSection(this.select.section)
        if (this.select.place) {
          this.seatSeatAssign()
        }
      }
    },
    async setSection (section) {
      if (section) {
        this.$store.commit('loading', true)
        const checkSection = await this.$store.dispatch('reservation/checkReservationSection', {
          property: this.info.id,
          date: this.reserve.date,
          time: this.reserve.time.id,
          section: section
        })
        if (checkSection) {
          this.select.section = section
          this.remainSeat = checkSection
        } else {
          this.select.section = null
          this.remainSeat = null
          alert(this.locale.alert_data_load_fail)
        }
        this.$store.commit('loading', false)
      } else {
        this.select.section = null
        this.remainSeat = null
      }
    },
    setPlace (place) {
      if (place) {
        this.select.place = place
        this.seatSeatAssign()
      } else {
        this.reserve.seats = null
      }
    },
    seatSeatAssign () {
      const isStaff = this.isStaff
      const isMultiple = this.info.multiple
      const count = this.seatCount
      const seatInfo = this.seat && this.seat.list[0] // staff인 경우 인원수만큼 name: '-1'인 seat를 생성
      const seat = this.seat && this.seat.list && this.seat.list.filter(x => !x.disabled).filter((x, index) => index < count).map(x => {
        return {
          placeId: x.placeId,
          placeName: x.placeName,
          seatId: x.seatId,
          seatName: x.name
        }
      })
      const addedLength = (isStaff && count - seat.length) || 0
      const addedSeat = addedLength > 0 // 모자란 자리만큼 예약
        ? makeSeat(addedLength, seatInfo)
        : []
      function langObj (obj) {
        let result = {}
        const keys = Object.keys(obj)
        for (let i = 0; i < keys.length; i++) {
          result = { ...result, [keys[i]]: '-1' }
        }
        return result
      }
      function makeSeat (addedLength, seatInfo) {
        let seatList = []
        for (let i = 0; i < count; i++) {
          seatList.push({
            placeId: seatInfo.placeId,
            placeName: seatInfo.placeName,
            seatId: '-1',
            seatName: langObj(seatInfo.name)
          })
        }
        return seatList
      }
      this.setSeats(seat.concat(addedSeat))
    },
    setSeats (data) {
      const sectionId = this.section && this.section.active && this.section.active.id
      const sectionName = sectionId && this.section.active.name
      this.reserve.seats = data
      ? data.map(x => {
          return {
            section: {
              id: sectionId,
              name: sectionName
            },
            place: {
              id: x.placeId,
              name: x.placeName
            },
            seat: {
              id: x.seatId,
              name: x.seatName
            }
          }
        })
      : null
      this.select.place = data && data[0] ? data[0].placeId : null
      this.checkReservation()
    },
    async checkReservation () {
      const validate = this.validate
      if (validate && !validate.isStaff && validate.date && validate.time && validate.person && validate.section && validate.place && validate.seat) {
        const checkReservation = await this.$store.dispatch('reservation/checkReservation', {
          property: this.info.id,
          date: this.reserve.date,
          time: this.reserve.time.id,
          seats: this.reserve.seats,
        })
        if (checkReservation && checkReservation.code) {
          const code = this.getResultData(checkReservation.code)
          if (checkReservation.code === 200) {
            this.checkDateMessage = null
            this.checkComplete = true
          } else {
            this.checkDateMessage = this.locale[`alert_reservation_${code}`]
            this.checkComplete = false
          }
        } else {
          this.checkDateMessage = this.locale.alert_data_load_fail
          this.checkComplete = false
          alert(this.locale.alert_data_load_fail)
        }
      } else if (validate.isStaff) {
        this.checkDateMessage = null
        this.checkComplete = true
      }
    },
    setUserInfo (data) {
      const target = data.target
      const info = data.info
      this.validatePhone = this.info.bookerTel && data.phoneIsValid
      this.reserve[target] = info
    },
    confirmSummary () {
      const getTimeLabel = () => {
        const timeId = this.reserve.time && this.reserve.time.id
        const timeInfo = this.info.time && this.info.time.find(x => x.timeId === timeId)
        return timeInfo && timeInfo.label
      }
      this.summary = {
        ...this.reserve,
        time: {
          ...this.reserve.time,
          label: getTimeLabel()
        },
        spaceName: this.info.name,
        sectionName: this.section.active.name,
        placeName: this.place.active.name,
        autoAssign: this.section.active.autoAssign,
        ...!this.section.active.autoAssign && {
          showPlaceName: this.section.active.showPlaceName,
          showSeatName: this.section.active.showSeatName
        }
      }
    },
    submit () {
      const lang = this.ln
      const data = this.summary
      const location = window.location
      const siteInfo = this.$store.state.authentication
      const rid = siteInfo.rid ? `&rid=${siteInfo.rid}` : ''
      const ota = siteInfo.space && siteInfo.k ? `&space=${siteInfo.space}&k=${siteInfo.k}` : ''
      const setReservationData = {
        returnUrl: `${location.origin}${location.pathname}?site=${siteInfo.site}${rid}${ota}&frame=false#/${lang}/reservation/check/`,
        payload: {
          userLanguage: lang,
          date: this.reserve.date,
          time: this.reserve.time,
          person: this.reserve.person,
          space: {
            id: this.info.id,
            name: this.info.name,
            space: this.info.space
          },
          seats: this.reserve.seats,
          sectionName: this.summary.sectionName,
          placeName: this.summary.placeName,
          firstName: this.reserve.firstName,
          lastName: this.reserve.lastName,
          ...this.reserve.phone && {
            phone: this.reserve.phone
          },
          ...this.reserve.email && {
            email: this.reserve.email
          }
        }
      }
      this.$store.commit('loading', true)
      this.$store.dispatch('reservation/setReservation', setReservationData).then((res) => {
        this.summary = null
        if (res && res.status && res.status === 200) {
          const reservationId = res.data.message
          if (reservationId) {
            this.$router.push({ name: 'reservationComplete', params: { lang: lang, reservationId: reservationId } })
          } else {
            alert(this.locale.alert_fail_reservation)
          }
        } else {
          if (res.response && res.response.data && res.response.data.message) {
            const errorCode = res.response.data.message.code || 500
            const errorMessage = this.getResultData(errorCode)
            alert(this.locale[`alert_reservation_${errorMessage}`])
          } else {
            alert(this.locale.alert_fail_reservation)
          }
        }
        this.$store.commit('loading', false)
        this.$emit('init')
      })
    }
  }
}
</script>
